import { Injectable } from '@angular/core';

export type TenantResolution = {
  hostname: string;
  tenantSlug: string;
};

type TenantResolverInput =
  | string
  | {
      hostname?: string;
      pathname?: string;
      search?: string;
    };

@Injectable({ providedIn: 'root' })
export class TenantResolverService {
  private readonly slugPattern = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
  private readonly reservedSubdomains = new Set(['www', 'api', 'api-dev', 'dev-api']);
  private readonly reservedPathSegments = new Set([
    'admin',
    'assets',
    'cardapio',
    'categorias',
    'checkout',
    'configuracao',
    'dashboard',
    'entregas',
    'extras',
    'financial',
    'imagens',
    'item',
    'login',
    'orders',
    'pedidos',
    'produtos',
    'vitrine',
  ]);

  resolve(input: TenantResolverInput = window.location): TenantResolution {
    const location = this.normalizeInput(input);
    const normalizedHostname = location.hostname.trim().toLowerCase();

    const fromQuery = this.extractFromQuery(location.search);
    if (fromQuery) {
      return {
        hostname: normalizedHostname || 'localhost',
        tenantSlug: fromQuery,
      };
    }

    const fromHostname = this.extractFromHostname(normalizedHostname);
    if (fromHostname) {
      return {
        hostname: normalizedHostname,
        tenantSlug: fromHostname,
      };
    }

    const fromPath = this.extractFromPath(location.pathname);
    if (fromPath) {
      return {
        hostname: normalizedHostname || 'localhost',
        tenantSlug: fromPath,
      };
    }

    return {
      hostname: normalizedHostname || 'localhost',
      tenantSlug: 'default',
    };
  }

  private normalizeInput(input: TenantResolverInput): {
    hostname: string;
    pathname: string;
    search: string;
  } {
    if (typeof input === 'string') {
      return {
        hostname: input,
        pathname: '/',
        search: '',
      };
    }

    return {
      hostname: input.hostname ?? '',
      pathname: input.pathname ?? '/',
      search: input.search ?? '',
    };
  }

  private extractFromQuery(search: string): string | null {
    if (!search) return null;

    const params = new URLSearchParams(search);
    return this.normalizeTenantSlug(params.get('tenant'));
  }

  private extractFromHostname(hostname: string): string | null {
    if (!hostname || hostname === 'localhost' || hostname === '127.0.0.1') {
      return null;
    }

    const parts = hostname.split('.').filter(Boolean);
    if (parts.length < 2) {
      return null;
    }
    if (parts.length === 2 && parts[1] !== 'localhost') {
      return null;
    }

    const firstLabel = parts[0];
    if (!firstLabel || this.reservedSubdomains.has(firstLabel)) {
      return null;
    }

    return this.normalizeTenantSlug(firstLabel);
  }

  private extractFromPath(pathname: string): string | null {
    const [firstSegment] = pathname
      .split('?')[0]
      .split('/')
      .filter(Boolean);

    if (!firstSegment || this.reservedPathSegments.has(firstSegment)) {
      return null;
    }

    return this.normalizeTenantSlug(firstSegment);
  }

  private normalizeTenantSlug(value?: string | null): string | null {
    const normalized = value?.trim().toLowerCase() ?? '';
    if (!normalized) {
      return null;
    }

    return this.isValidTenantSlug(normalized) ? normalized : null;
  }

  private isValidTenantSlug(value: string): boolean {
    return this.slugPattern.test(value);
  }
}
